<template>
    <div class="analytics-finding-evidence-chart w-full space-y-4">
        <div v-if="isLoading" class="text-center py-10">
            <div class="inline-flex items-center text-sm text-gray-600">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading sensor readings…
            </div>
        </div>

        <div v-else-if="errorMessage" class="rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-center">
            <p class="text-sm font-medium text-red-800">{{ errorTitle }}</p>
            <p class="mt-1 text-sm text-red-700">{{ errorMessage }}</p>
            <button type="button" class="mt-3 text-sm font-medium text-red-800 underline" @click="loadData">
                Try again
            </button>
        </div>

        <template v-else>
            <div v-if="summaryCards.length" class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div
                    v-for="(card, idx) in summaryCards"
                    :key="idx"
                    class="rounded-lg border px-4 py-3"
                    :class="card.tone === 'highlight' ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'"
                >
                    <div class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{{ card.label }}</div>
                    <div class="mt-1 text-lg font-semibold text-gray-900">{{ card.value }}</div>
                    <div v-if="card.hint" class="mt-0.5 text-xs text-gray-600">{{ card.hint }}</div>
                </div>
            </div>

            <div v-if="mode === 'regime'" class="space-y-4">
                <div class="relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <div ref="chartContainer" :style="graphHeight" class="w-full"></div>
                    <div v-if="annotationLabel" class="pointer-events-none absolute top-3 right-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900 shadow-sm">
                        {{ annotationLabel }}
                    </div>
                </div>
                <analytics-volatility-regime-timeline :meter-id="meterId"></analytics-volatility-regime-timeline>
            </div>

            <div v-else-if="mode !== 'peer'" class="relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div v-if="findingBandLabel" class="mb-2 flex items-center gap-2 rounded-md border border-lime-300 bg-lime-50/90 px-3 py-2 text-xs text-lime-950">
                    <span class="inline-block h-2.5 flex-1 max-w-[120px] rounded-sm bg-lime-300/80 border border-lime-400"></span>
                    <span class="font-medium">{{ findingBandLabel }}</span>
                </div>
                <div ref="chartContainer" :style="graphHeight" class="w-full"></div>
                <div v-if="annotationLabel" class="pointer-events-none absolute top-14 right-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900 shadow-sm">
                    {{ annotationLabel }}
                </div>
            </div>

            <div v-else class="relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div ref="chartContainer" :style="graphHeight" class="w-full"></div>
            </div>

            <p v-if="evidenceCaption" class="text-sm text-gray-700 leading-relaxed">{{ evidenceCaption }}</p>

            <div v-if="legendItems.length" class="flex flex-wrap gap-4 text-xs text-gray-600">
                <span v-for="(item, idx) in legendItems" :key="idx" class="inline-flex items-center gap-1.5">
                    <span class="inline-block h-2.5 w-2.5 rounded-full" :style="{ background: item.color }"></span>
                    {{ item.label }}
                </span>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import { SonicuCoreMixin } from '@/mixins/SonicuCoreMixin';
import { amchartsMixin } from '@/mixins/amchartsMixin';
import { maxChartPointsForSpan, simplifyChartPoints, simplifyChartPointsForRange } from '@/utils/simplifyChartPoints';
import {
    CHART_MAX_POINTS,
    DRIFT_METRICS,
    METER_LINE_COLOR,
    averageOfValues,
    estimateSwing as sharedEstimateSwing,
    fetchMeterReadings,
} from '@/utils/analyticsFindingsShared';

import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

const FINDING_BAND_COLOR = '#fde047';
const BEFORE_BAND_COLOR = '#dbeafe';
const AFTER_BAND_COLOR = '#fef3c7';

export default {
    name: 'AnalyticsFindingEvidenceChart',
    mixins: [SonicuCoreMixin, amchartsMixin],
    props: {
        meterId: {
            type: Number,
            required: true,
        },
        metricKey: {
            type: String,
            required: true,
        },
        finding: {
            type: Object,
            default: null,
        },
        categoryName: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isLoading: true,
            errorMessage: '',
            errorTitle: 'Unable to load chart',
            summaryCards: [],
            evidenceCaption: '',
            legendItems: [],
            annotationLabel: '',
            findingBandLabel: '',
            units: '',
            mode: 'pattern',
            rawPoints: [],
            dataStartMs: null,
            dataEndMs: null,
            suppressRangeUpdate: false,
            _chart: null,
            _xAxis: null,
            _series: null,
            _axisRanges: [],
        };
    },
    created() {
        Object.defineProperty(this, 'root', {
            value: null,
            writable: true,
            enumerable: false,
            configurable: true,
        });
        this.initializeAM5Charts();
    },
    mounted() {
        this.$nextTick(() => {
            this.loadData();
        });
    },
    beforeUnmount() {
        this.disposeChart();
    },
    computed: {
        graphHeight() {
            const clientHeight = Math.max(document.documentElement.clientHeight - 420, 420);
            return { height: `${clientHeight}px`, width: '100%' };
        },
        findingType() {
            return String(this.finding?.type || '');
        },
        detectedAtMs() {
            const raw = this.finding?.detected_at;
            if (!raw) {
                return null;
            }
            const ts = new Date(raw).getTime();
            return Number.isNaN(ts) ? null : ts;
        },
    },
    methods: {
        resolveMode() {
            const key = String(this.metricKey || '');
            const type = this.findingType;

            if (type === 'peer_outlier' || key === 'category_outlier') {
                return 'peer';
            }
            if (type === 'volatility_regime' || key === 'volatility_regime') {
                return 'regime';
            }
            if (type === 'volatility' || key === 'volatility') {
                return 'volatility';
            }
            if (DRIFT_METRICS.has(key) || type === 'trend_shift' || type === 'change_point') {
                return 'drift';
            }

            return 'pattern';
        },

        async loadData() {
            this.isLoading = true;
            this.errorMessage = '';
            this.summaryCards = [];
            this.evidenceCaption = '';
            this.legendItems = [];
            this.annotationLabel = '';
            this.findingBandLabel = '';
            this.mode = this.resolveMode();

            try {
                if (this.mode === 'peer') {
                    await this.loadPeerEvidence();
                    return;
                }

                if (this.mode === 'regime') {
                    await this.loadRegimeEvidence();
                    return;
                }

                await this.loadReadingEvidence();
            } catch (error) {
                this.handleError(
                    'Failed to load evidence',
                    error.response?.data?.message || error.message || 'Unexpected error',
                );
            }
        },

        async fetchRawReadings(days = 90) {
            return fetchMeterReadings(this.company, this.meterId, days);
        },

        async fetchChangePoints() {
            try {
                const response = await axios.get(
                    `${this.company.buildCompanyApiInternalRoute('analytics-meter')}/${this.meterId}`,
                    { params: { metric: 'hybrid_trend_shift' } },
                );
                return response.data?.change_points || [];
            } catch {
                return [];
            }
        },

        async loadReadingEvidence() {
            const { points, units } = await this.fetchRawReadings(90);
            if (!points.length) {
                this.handleError('No readings', 'No sensor readings are available for the last 90 days.');
                return;
            }

            this.units = units;
            const changePoints = this.mode === 'drift' ? await this.fetchChangePoints() : [];
            const overlay = this.buildOverlayConfig(points, changePoints);

            if (this.mode === 'drift') {
                this.buildDriftSummary(points, overlay.splitMs);
            } else if (this.mode === 'volatility') {
                this.buildVolatilitySummary(points, overlay.highlightStartMs, overlay.highlightEndMs);
            } else {
                this.buildPatternSummary(overlay);
            }

            this.isLoading = false;
            this.$nextTick(() => {
                this.renderMeterReadingChart(points, overlay);
            });
        },

        async loadRegimeEvidence() {
            const { points, units } = await this.fetchRawReadings(90);
            if (!points.length) {
                this.handleError('No readings', 'No sensor readings are available for the last 90 days.');
                return;
            }

            let transitions = [];
            try {
                const response = await axios.get(
                    this.company.buildCompanyApiInternalRoute(`analytics/meters/${this.meterId}/volatility-regime-timeline`),
                    { params: { range: '90d' } },
                );
                transitions = Array.isArray(response.data?.transitions) ? response.data.transitions : [];
            } catch {
                transitions = [];
            }

            this.units = units;
            const latestTransition = transitions.length
                ? [...transitions].sort((a, b) => (b.timestamp_ms ?? 0) - (a.timestamp_ms ?? 0))[0]
                : null;

            const overlay = {
                markers: transitions.map((t) => ({
                    atMs: Number(t.timestamp_ms),
                    label: `${this.prettyRegime(t.from_regime)} → ${this.prettyRegime(t.to_regime)}`,
                    color: '#d97706',
                })),
                zoomStartMs: latestTransition ? Number(latestTransition.timestamp_ms) - (21 * 86400000) : null,
                zoomEndMs: points[points.length - 1].date,
            };

            if (latestTransition) {
                overlay.highlightStartMs = Number(latestTransition.timestamp_ms);
                overlay.highlightEndMs = points[points.length - 1].date;
                overlay.highlightLabel = 'Since regime change';
            }

            this.buildRegimeCaption();
            this.annotationLabel = this.finding?.label || 'Regime transition';
            this.isLoading = false;

            this.$nextTick(() => {
                this.renderMeterReadingChart(points, overlay);
            });
        },

        buildOverlayConfig(points, changePoints) {
            const endMs = points[points.length - 1].date;
            const startMs = points[0].date;

            if (this.mode === 'drift') {
                const splitMs = this.resolveSplitTimestamp(points, changePoints);
                return {
                    splitMs,
                    zoomStartMs: splitMs ? splitMs - (30 * 86400000) : null,
                    zoomEndMs: splitMs ? Math.min(endMs, splitMs + (45 * 86400000)) : null,
                    markerLabel: 'Shift detected',
                };
            }

            if (this.mode === 'volatility') {
                const recentDays = 21;
                const highlightStartMs = Math.max(
                    startMs,
                    endMs - (recentDays * 86400000),
                );
                return {
                    highlightStartMs,
                    highlightEndMs: endMs,
                    highlightLabel: 'Period we flagged',
                    zoomStartMs: highlightStartMs - (7 * 86400000),
                    zoomEndMs: endMs,
                    markerLabel: 'Unusual variability',
                };
            }

            const highlightStartMs = Math.max(startMs, endMs - (21 * 86400000));
            return {
                highlightStartMs,
                highlightEndMs: endMs,
                highlightLabel: 'Recent pattern under review',
                zoomStartMs: highlightStartMs - (7 * 86400000),
                zoomEndMs: endMs,
            };
        },

        buildDriftSummary(points, splitMs) {
            if (!splitMs) {
                this.evidenceCaption = this.finding?.detail || 'Review how this point has been reading over the analysis window.';
                this.legendItems = [
                    { color: METER_LINE_COLOR, label: 'Sensor readings' },
                ];
                return;
            }

            const MIN_AFTER_SAMPLE = 30;
            const before = points.filter((row) => row.date < splitMs);
            const after = points.filter((row) => row.date >= splitMs);
            const beforeAvg = before.length ? this.average(before.map((r) => r.value)) : null;
            const afterAvg = after.length ? this.average(after.map((r) => r.value)) : null;
            const afterSampleTooSmall = after.length > 0 && after.length < MIN_AFTER_SAMPLE;
            const delta = (beforeAvg != null && afterAvg != null && !afterSampleTooSmall) ? afterAvg - beforeAvg : null;
            const pct = beforeAvg && delta != null ? (delta / beforeAvg) * 100 : null;

            this.summaryCards = [
                {
                    label: 'Typical before',
                    value: beforeAvg != null ? this.formatNumber(beforeAvg) : '—',
                    hint: before.length ? `${before.length} readings averaged` : null,
                },
                {
                    label: 'Typical after',
                    value: afterAvg != null ? this.formatNumber(afterAvg) : '—',
                    hint: after.length
                        ? `${after.length} readings averaged${afterSampleTooSmall ? ' (limited sample)' : ''}`
                        : null,
                    tone: 'highlight',
                },
                {
                    label: 'Change',
                    value: delta != null ? `${delta >= 0 ? '+' : ''}${this.formatNumber(delta)}` : '—',
                    hint: pct != null
                        ? `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% vs before`
                        : (afterSampleTooSmall ? 'Too few readings after the shift to compare reliably' : null),
                    tone: 'highlight',
                },
            ];

            const when = new Date(splitMs).toLocaleDateString();
            this.findingBandLabel = `After the shift · since ${when}`;
            this.annotationLabel = this.finding?.label || 'Reading shift';
            this.evidenceCaption = this.finding?.why
                || `The vertical marker shows where readings changed level around ${when}. The highlighted band is the period after the shift.`;
            this.legendItems = [
                { color: METER_LINE_COLOR, label: 'Sensor readings (same view as point graph)' },
                { color: BEFORE_BAND_COLOR, label: 'Before the shift' },
                { color: AFTER_BAND_COLOR, label: 'After the shift' },
                { color: '#d97706', label: 'Shift marker' },
            ];
        },

        buildVolatilitySummary(points, highlightStartMs, highlightEndMs) {
            const baselinePoints = points.filter((row) => row.date < highlightStartMs);
            const recentPoints = points.filter((row) => row.date >= highlightStartMs);
            const baselineSwing = this.estimateSwing(baselinePoints);
            const recentSwing = this.estimateSwing(recentPoints);
            const pctAbove = baselineSwing > 0 ? ((recentSwing - baselineSwing) / baselineSwing) * 100 : null;

            this.summaryCards = [
                {
                    label: 'Typical variability',
                    value: this.formatNumber(baselineSwing),
                    hint: 'Before the flagged window',
                },
                {
                    label: 'Recent variability',
                    value: this.formatNumber(recentSwing),
                    hint: 'Inside the highlighted window',
                    tone: 'highlight',
                },
                {
                    label: 'Increase',
                    // `|| 0` normalizes -0 so a hair-below-baseline swing reads "0%", not "-0%"
                    value: pctAbove != null ? `${Math.round(pctAbove) > 0 ? '+' : ''}${Math.round(pctAbove) || 0}%` : '—',
                    hint: 'Recent vs typical',
                    tone: 'highlight',
                },
            ];

            const startLabel = new Date(highlightStartMs).toLocaleDateString();
            const endLabel = new Date(highlightEndMs).toLocaleDateString();
            this.findingBandLabel = `Flagged period · ${startLabel} – ${endLabel}`;
            this.annotationLabel = this.finding?.label || 'Elevated variability';
            this.evidenceCaption = this.finding?.why
                || 'The highlighted band marks when readings started swinging more than this point\'s usual pattern.';
            this.legendItems = [
                { color: METER_LINE_COLOR, label: 'Sensor readings (same view as point graph)' },
                { color: FINDING_BAND_COLOR, label: 'Flagged period' },
            ];
        },

        buildPatternSummary(overlay) {
            this.findingBandLabel = overlay.highlightLabel || 'Recent pattern under review';
            this.annotationLabel = this.finding?.label || 'Unusual pattern';
            this.evidenceCaption = this.finding?.detail
                || this.finding?.why
                || 'The highlighted band shows the recent readings that triggered this finding.';
            this.legendItems = [
                { color: METER_LINE_COLOR, label: 'Sensor readings (same view as point graph)' },
                { color: FINDING_BAND_COLOR, label: 'Flagged period' },
            ];
        },

        buildRegimeCaption() {
            this.evidenceCaption = this.finding?.why
                || 'Markers show when this point moved between stability states. Compare the raw readings above with the regime timeline below.';
            this.legendItems = [
                { color: METER_LINE_COLOR, label: 'Sensor readings' },
                { color: '#d97706', label: 'Regime transition' },
            ];
        },

        renderMeterReadingChart(points, overlay) {
            this.disposeChart();
            this.rawPoints = points;
            this.dataStartMs = points[0]?.date ?? null;
            this.dataEndMs = points[points.length - 1]?.date ?? null;

            this.$nextTick(() => {
                const container = this.$refs.chartContainer;
                if (!container) {
                    this.handleError('Chart unavailable', 'The chart could not be displayed. Refresh the page and try again.');
                    return;
                }

                this.root = am5.Root.new(container);
                this.root.setThemes([am5themes_Animated.new(this.root)]);

                const chart = this.root.container.children.push(
                    am5xy.XYChart.new(this.root, {
                        panX: true,
                        panY: false,
                        wheelX: 'panX',
                        wheelY: 'zoomX',
                        pinchZoomX: true,
                        paddingTop: 12,
                        paddingRight: 16,
                        paddingBottom: 8,
                        paddingLeft: 8,
                        layout: this.root.verticalLayout,
                    }),
                );

                const xAxis = chart.xAxes.push(
                    am5xy.DateAxis.new(this.root, {
                        baseInterval: { timeUnit: 'second', count: 1 },
                        renderer: am5xy.AxisRendererX.new(this.root, {
                            minGridDistance: 60,
                        }),
                        tooltip: am5.Tooltip.new(this.root, {}),
                    }),
                );
                this._xAxis = xAxis;

                const yAxis = chart.yAxes.push(
                    am5xy.ValueAxis.new(this.root, {
                        renderer: am5xy.AxisRendererY.new(this.root, {
                            minGridDistance: 30,
                        }),
                    }),
                );
                yAxis.get('renderer').labels.template.adapters.add('text', (text) => {
                    if (text === undefined || text === null || !this.units) {
                        return text;
                    }
                    return `${text} ${this.units}`;
                });

                const series = chart.series.push(
                    am5xy.LineSeries.new(this.root, {
                        name: 'Readings',
                        xAxis,
                        yAxis,
                        valueYField: 'value',
                        valueXField: 'date',
                        connect: true,
                        tensionX: 1,
                        tooltip: am5.Tooltip.new(this.root, {
                            labelText: `{valueY.formatNumber('#.##')}${this.units ? ` ${this.units}` : ''}\n{valueX.formatDate('MMM d, yyyy h:mm a')}`,
                        }),
                    }),
                );
                series.set('stroke', am5.color(METER_LINE_COLOR));
                series.strokes.template.setAll({
                    stroke: am5.color(METER_LINE_COLOR),
                    strokeWidth: 1.5,
                });
                series.fills.template.setAll({ visible: false });
                series.data.setAll(simplifyChartPoints(points, CHART_MAX_POINTS));
                this._series = series;

                if (overlay.splitMs && points.length) {
                    this.addDateRange(xAxis, points[0].date, overlay.splitMs, am5.color(BEFORE_BAND_COLOR), 0.35);
                    this.addDateRange(xAxis, overlay.splitMs, points[points.length - 1].date, am5.color(AFTER_BAND_COLOR), 0.45);
                    this.addDateMarker(xAxis, overlay.splitMs, overlay.markerLabel || 'Shift detected', '#d97706');
                }

                if (overlay.highlightStartMs && overlay.highlightEndMs) {
                    this.addDateRange(
                        xAxis,
                        overlay.highlightStartMs,
                        overlay.highlightEndMs,
                        am5.color(FINDING_BAND_COLOR),
                        0.42,
                        overlay.highlightLabel || 'Flagged period',
                    );
                }

                (overlay.markers || []).forEach((marker) => {
                    this.addDateMarker(xAxis, marker.atMs, marker.label, marker.color || '#d97706');
                });

                const scrollbar = am5.Scrollbar.new(this.root, {
                    orientation: 'horizontal',
                    height: 36,
                });
                chart.set('scrollbarX', scrollbar);

                const sbChart = scrollbar.chart;
                if (sbChart) {
                    const sbX = sbChart.xAxes.push(
                        am5xy.DateAxis.new(this.root, {
                            baseInterval: { timeUnit: 'second', count: 1 },
                            renderer: am5xy.AxisRendererX.new(this.root, {}),
                        }),
                    );
                    const sbY = sbChart.yAxes.push(
                        am5xy.ValueAxis.new(this.root, {
                            renderer: am5xy.AxisRendererY.new(this.root, {}),
                        }),
                    );
                    const sbSeries = sbChart.series.push(
                        am5xy.LineSeries.new(this.root, {
                            xAxis: sbX,
                            yAxis: sbY,
                            valueYField: 'value',
                            valueXField: 'date',
                            stroke: am5.color(METER_LINE_COLOR),
                        }),
                    );
                    sbSeries.data.setAll(simplifyChartPoints(points, CHART_MAX_POINTS));

                    if (overlay.highlightStartMs && overlay.highlightEndMs) {
                        this.addDateRange(
                            sbX,
                            overlay.highlightStartMs,
                            overlay.highlightEndMs,
                            am5.color('#bef264'),
                            0.65,
                        );
                    }
                }

                chart.set('cursor', am5xy.XYCursor.new(this.root, {
                    behavior: 'zoomX',
                    xAxis,
                }));

                if (overlay.zoomStartMs != null && overlay.zoomEndMs != null) {
                    this.suppressRangeUpdate = true;
                    xAxis.zoomToDates(new Date(overlay.zoomStartMs), new Date(overlay.zoomEndMs));
                    this.applySeriesDataForRange(overlay.zoomStartMs, overlay.zoomEndMs);
                    window.setTimeout(() => {
                        this.suppressRangeUpdate = false;
                    }, 150);
                }

                xAxis.events.on('rangechangeended', () => {
                    if (this.suppressRangeUpdate) {
                        return;
                    }
                    this.updateSeriesForVisibleRange();
                });

                this._chart = chart;
            });
        },

        updateSeriesForVisibleRange() {
            if (!this._series || !this._xAxis || !this.rawPoints.length) {
                return;
            }

            const startMs = this._xAxis.getPrivate('selectionMin');
            const endMs = this._xAxis.getPrivate('selectionMax');
            this.applySeriesDataForRange(startMs, endMs);
        },

        applySeriesDataForRange(startMs, endMs) {
            if (!this._series || !this.rawPoints.length) {
                return;
            }

            const rangeStart = startMs ?? this.dataStartMs;
            const rangeEnd = endMs ?? this.dataEndMs;
            const fullSpan = (this.dataEndMs ?? 0) - (this.dataStartMs ?? 0);
            const visibleSpan = (rangeEnd ?? 0) - (rangeStart ?? 0);
            const maxPoints = maxChartPointsForSpan(visibleSpan, fullSpan, CHART_MAX_POINTS);
            const simplified = simplifyChartPointsForRange(
                this.rawPoints,
                rangeStart,
                rangeEnd,
                maxPoints,
            );

            this._series.data.clear();
            this._series.data.setAll(simplified);
        },

        async loadPeerEvidence() {
            if (!this.categoryName) {
                this.handleError('No category', 'This meter is not assigned to a category for peer comparison.');
                return;
            }

            const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
            const response = await axios.post(
                this.company.buildCompanyRoute('settings/advanced-analytics/category-detailed-comparison'),
                {
                    category: this.categoryName,
                    metric_type: 'volatility',
                    dimension: 'mean',
                    days: 90,
                },
                {
                    headers: token ? { 'X-CSRF-TOKEN': token } : {},
                },
            );

            if (!response.data.success) {
                this.handleError('Peer comparison unavailable', response.data.message || 'Could not load category comparison.');
                return;
            }

            const meters = (response.data.meters || [])
                .filter((row) => row.dimension_value != null)
                .sort((a, b) => (b.z_score ?? 0) - (a.z_score ?? 0));

            if (!meters.length) {
                this.handleError('No peer data', 'No comparison values are available for this category yet.');
                return;
            }

            const current = meters.find((row) => Number(row.meter_id) === Number(this.meterId));
            const categoryMean = response.data.dimension_summary?.mean ?? null;

            this.summaryCards = [
                {
                    label: 'This meter vs category',
                    value: current?.z_score != null ? `${Number(current.z_score).toFixed(1)}σ from peers` : '—',
                    hint: current?.rank != null ? `Rank ${current.rank} of ${meters.length}` : null,
                    tone: 'highlight',
                },
                {
                    label: 'Category typical reading',
                    value: categoryMean != null ? this.formatNumber(categoryMean) : '—',
                    hint: 'Mean signal across similar equipment',
                },
                {
                    label: 'This meter\'s reading',
                    value: current?.dimension_value != null ? this.formatNumber(current.dimension_value) : '—',
                    hint: current?.meter_name || 'Current meter',
                    tone: 'highlight',
                },
            ];

            this.evidenceCaption = this.finding?.why
                || 'This point reads differently from others in the same equipment group.';
            this.legendItems = [
                { color: '#d97706', label: 'This meter' },
                { color: '#94a3b8', label: 'Other equipment in category' },
            ];

            const chartRows = meters.slice(0, 12).map((row) => ({
                label: row.meter_name,
                value: Number(row.dimension_value),
                isCurrent: Number(row.meter_id) === Number(this.meterId),
            }));

            this.isLoading = false;
            this.$nextTick(() => {
                this.renderPeerBarChart(chartRows, categoryMean);
            });
        },

        resolveSplitTimestamp(points, changePoints) {
            // A split at or beyond the last reading leaves nothing "after" it, so
            // ignore timestamps that postdate the data (detected_at is the analysis
            // run time and can land there).
            const lastMs = points.length ? points[points.length - 1].date : null;
            const usable = (ms) => ms != null && (lastMs == null || ms < lastMs);

            if (usable(this.detectedAtMs)) {
                return this.detectedAtMs;
            }

            if (changePoints.length) {
                const sorted = [...changePoints].sort((a, b) => (b.timestamp_ms ?? 0) - (a.timestamp_ms ?? 0));
                const candidate = sorted.find((row) => usable(row.timestamp_ms));
                return candidate?.timestamp_ms ?? null;
            }

            return null;
        },

        addDateRange(xAxis, startMs, endMs, color, opacity, label = null) {
            const range = xAxis.createAxisRange(xAxis.makeDataItem({ value: startMs }));
            range.set('endValue', endMs);
            range.get('axisFill').setAll({
                fill: color,
                fillOpacity: opacity,
                visible: true,
            });
            if (label) {
                range.get('label').setAll({
                    text: label,
                    inside: true,
                    location: 0.5,
                    paddingTop: 4,
                    paddingBottom: 4,
                    fontSize: 11,
                    fill: am5.color('#92400e'),
                });
            }
            this._axisRanges.push(range);
        },

        addDateMarker(xAxis, atMs, label, color) {
            const range = xAxis.createAxisRange(xAxis.makeDataItem({ value: atMs }));
            range.get('grid').setAll({
                stroke: am5.color(color),
                strokeWidth: 2,
                strokeOpacity: 0.95,
            });
            range.get('label').setAll({
                text: label,
                rotation: -90,
                centerY: am5.p100,
                centerX: am5.p0,
                paddingTop: 6,
                fontSize: 11,
                fontWeight: '600',
                fill: am5.color(color),
            });
            this._axisRanges.push(range);
        },

        renderPeerBarChart(rows, categoryMean) {
            this.disposeChart();

            this.$nextTick(() => {
                const container = this.$refs.chartContainer;
                if (!container) {
                    this.handleError('Chart unavailable', 'The chart could not be displayed. Refresh the page and try again.');
                    return;
                }

                this.root = am5.Root.new(container);
                this.root.setThemes([am5themes_Animated.new(this.root)]);

                const chart = this.root.container.children.push(
                    am5xy.XYChart.new(this.root, {
                        panX: false,
                        panY: true,
                        wheelY: 'panY',
                        layout: this.root.verticalLayout,
                        paddingTop: 20,
                    }),
                );

                const yAxis = chart.yAxes.push(
                    am5xy.CategoryAxis.new(this.root, {
                        categoryField: 'label',
                        renderer: am5xy.AxisRendererY.new(this.root, {
                            inversed: true,
                            cellStartLocation: 0.1,
                            cellEndLocation: 0.9,
                        }),
                    }),
                );
                yAxis.data.setAll(rows);

                const xAxis = chart.xAxes.push(
                    am5xy.ValueAxis.new(this.root, {
                        renderer: am5xy.AxisRendererX.new(this.root, {}),
                    }),
                );

                if (categoryMean != null) {
                    const range = xAxis.createAxisRange(xAxis.makeDataItem({ value: categoryMean }));
                    range.get('grid').setAll({
                        stroke: am5.color('#6366f1'),
                        strokeDasharray: [4, 4],
                    });
                }

                const series = chart.series.push(
                    am5xy.ColumnSeries.new(this.root, {
                        xAxis,
                        yAxis,
                        valueXField: 'value',
                        categoryYField: 'label',
                    }),
                );
                series.columns.template.adapters.add('fill', (fill, target) => {
                    const ctx = target.dataItem?.dataContext;
                    return am5.color(ctx?.isCurrent ? '#d97706' : '#94a3b8');
                });
                series.columns.template.adapters.add('stroke', (stroke, target) => {
                    const ctx = target.dataItem?.dataContext;
                    return am5.color(ctx?.isCurrent ? '#b45309' : '#64748b');
                });
                series.data.setAll(rows);
                this._chart = chart;
            });
        },

        estimateSwing(points) {
            return sharedEstimateSwing(points);
        },

        average(values) {
            return averageOfValues(values);
        },

        prettyRegime(value) {
            if (!value || typeof value !== 'string') {
                return 'Unknown';
            }
            return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        },

        formatNumber(value) {
            if (value == null || !Number.isFinite(Number(value))) {
                return '—';
            }
            const n = Number(value);
            if (Math.abs(n) >= 100) {
                return n.toFixed(1);
            }
            if (Math.abs(n) >= 1) {
                return n.toFixed(2);
            }
            return n.toFixed(4);
        },

        disposeChart() {
            this._axisRanges = [];
            this._chart = null;
            this._xAxis = null;
            this._series = null;
            if (this.root) {
                this.root.dispose();
                this.root = null;
            }
        },

        handleError(title, message) {
            this.isLoading = false;
            this.errorTitle = title;
            this.errorMessage = message;
            this.disposeChart();
        },
    },
};
</script>

<style scoped>
.analytics-finding-evidence-chart {
    width: 100%;
}
</style>
